import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { TaxReturnExportButtons } from './TaxReturnExportButtons';

/** Renders the country-specific return form (sections → boxes) built by services/sales_tax_return_templates.py */
export const SalesTaxReturnForm = ({ form, formatAmount, startDate, endDate }) => {
  if (!form?.sections) return null;
  const rates = form.rates || {};
  const rateLabel = rates.source === 'state_table'
    ? `${rates.home_state} ${rates.state_rate}% state · ~${rates.combined_rate}% combined`
    : `${rates.standard_rate}% standard`;
  return (
    <Card data-testid="sales-tax-return-form">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-2">
          <span>{form.form}</span>
          <span className="flex items-center gap-2">
            <Badge variant="outline" data-testid="sales-tax-rate-badge">{form.tax_name} {rateLabel}</Badge>
            <Badge className={form.position === 'payable' ? 'bg-red-100 text-red-800' : form.position === 'refund' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'} data-testid="sales-tax-position-badge">
              {form.position === 'payable' ? 'Payable' : form.position === 'refund' ? 'Refund / credit' : 'Nil'} {formatAmount(Math.abs(form.net_tax || 0))}
            </Badge>
          </span>
        </CardTitle>
        <CardDescription data-testid="sales-tax-return-meta">
          File with <strong>{form.authority}</strong> · {form.frequency} · due {form.due_rule}
        </CardDescription>
        {startDate && endDate && (
          <div className="pt-2">
            <TaxReturnExportButtons endpoint="vat-return" startDate={startDate} endDate={endDate} label={`${form.form}`} testIdPrefix="vat-return-export" />
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        {form.sections.map((sec) => (
          <div key={sec.title} data-testid={`sales-tax-section-${sec.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{sec.title}</p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Box</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right w-48">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sec.lines.map((l) => (
                  <TableRow key={`${sec.title}-${l.box}-${l.label}`} className={l.emphasis ? 'bg-slate-50' : ''}>
                    <TableCell className="font-mono text-xs">{l.box}</TableCell>
                    <TableCell>
                      <span className={l.emphasis ? 'font-semibold' : ''}>{l.label}</span>
                      {l.derived && <Badge variant="outline" className="ml-2 text-[10px]">derived</Badge>}
                      {l.note && <p className="text-xs text-slate-500 mt-0.5">{l.note}</p>}
                    </TableCell>
                    <TableCell className={`text-right tabular-nums ${l.emphasis ? 'font-bold text-base' : 'font-medium'} ${l.value < 0 ? 'text-emerald-700' : ''}`}>
                      {l.kind === 'amount' ? (l.value < 0 ? `(${formatAmount(Math.abs(l.value))})` : formatAmount(l.value)) : l.value}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ))}
        {(rates.reduced_rates?.length > 0 || rates.zero_rated?.length > 0 || rates.exempt?.length > 0) && (
          <div className="text-xs text-slate-600 grid md:grid-cols-3 gap-3" data-testid="sales-tax-rate-schedule">
            {rates.reduced_rates?.length > 0 && <div><p className="font-semibold">Reduced rates</p>{rates.reduced_rates.map((r, i) => <p key={i}>{r.rate}% — {Array.isArray(r.applies_to) ? r.applies_to.join(', ') : r.applies_to}</p>)}</div>}
            {rates.zero_rated?.length > 0 && <div><p className="font-semibold">Zero-rated</p><p>{rates.zero_rated.join(', ')}</p></div>}
            {rates.exempt?.length > 0 && <div><p className="font-semibold">Exempt</p><p>{rates.exempt.join(', ')}</p></div>}
          </div>
        )}
        {form.notes?.map((n) => <p key={n} className="text-xs text-slate-500">{n}</p>)}
      </CardContent>
    </Card>
  );
};

export default SalesTaxReturnForm;
